/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Query, RestResponse } from '../model';
import { environment } from '../../environments/environment';
import { ApiHeaders } from './headers';
import { SystemType } from '../model/enums/system.type';

@Injectable()
export class ReportingProvider {

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Main API Endpoint to get Results
   * */
  getReports(query: Query, type: SystemType, page?: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/reporting?type=${type}&page=${page ? page : 1}`, query, ApiHeaders.getOptions());
  }

  exportReport(query: Query, type: SystemType, page?: number): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/reporting/export?type=${type}&page=${page}`, query, ApiHeaders.getOptionsJsonPlain());
  }

  getQuotes(query: Query, type: SystemType, page?: number): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/reporting/quotes?type=${type}&page=${page ? page : 1}`, query, ApiHeaders.getOptions());
  }

  exportQuotes(query: Query, type: SystemType, page?: number): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/reporting/quotes/export?type=${type}&page=${page}`, query, ApiHeaders.getOptionsJsonPlain());
  }

  exportErrorReport(query: any, sort: boolean, order: string, page?: number): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/pma/report/export?page=${page}&sort=${sort}&order=${order}`, query, ApiHeaders.getOptionsJsonPlain());
  }

  getReportAnalystTierErrors(query: Query, type: SystemType, fromDate: string, toDate: string, order: string, page?: number, size?: number, sort?: boolean,): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/reporting/analyst-tier-errors?type=${type}&page=${page}&size=${size}&sort=${sort}&order=${order}` + (fromDate ? '&fromDate=' + encodeURIComponent(fromDate) : "") + (toDate ? '&toDate=' + encodeURIComponent(toDate) : ""),
        {
          ERROR: query.FILTER.find(x => x.KEY == "ERROR")?.VALUE ? query.FILTER.find(x => x.KEY == "ERROR")?.VALUE : "",
          ASSIGNEE: query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE ? (query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE as any).ID || query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE : "",
          RATING: query.FILTER.find(x => x.KEY == "RATING")?.VALUE ? Number(query.FILTER.find(x => x.KEY == "RATING")?.VALUE) : '',
          COMPANY_NAME: query.FILTER.find(x => x.KEY == "COMPANY_NAME")?.VALUE ? query.FILTER.find(x => x.KEY == "COMPANY_NAME")?.VALUE : ""
        },
        ApiHeaders.getOptions());
  }

  exportAnalystTierErrors(query: Query, type: SystemType, fromDate: string, toDate: string, order: string, page?: number, size?: number, sort?: boolean): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/reporting/analyst-tier-errors/export?type=${type}&page=${page}&size=${size}&sort=${sort}&order=${order}` + (fromDate ? '&fromDate=' + encodeURIComponent(fromDate) : "") + (toDate ? '&toDate=' + encodeURIComponent(toDate) : ""),
        {
          ERROR: query.FILTER.find(x => x.KEY == "ERROR")?.VALUE ? query.FILTER.find(x => x.KEY == "ERROR")?.VALUE : "",
          ASSIGNEE: query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE ? (query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE as any).ID || query.FILTER.find(x => x.KEY == "ASSIGNEE")?.VALUE : "",
          RATING: query.FILTER.find(x => x.KEY == "RATING")?.VALUE ? Number(query.FILTER.find(x => x.KEY == "RATING")?.VALUE) : '',
          COMPANY_NAME: query.FILTER.find(x => x.KEY == "COMPANY_NAME")?.VALUE ? query.FILTER.find(x => x.KEY == "COMPANY_NAME")?.VALUE : ""
        }, ApiHeaders.getOptionsJsonPlain());
  }

  getDropOffReport(query:Query,type: SystemType, page: number, size: number, sort: boolean, order: string, fromDate: string="", toDate: string="",sortBy: string=""): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/reporting/drop-off-compare?type=${type}&page=${page}&size=${size}&sort=${sort}&order=${order}&sortBy=${sortBy}`+ '&fromDate=' + encodeURIComponent(fromDate) + '&toDate=' + encodeURIComponent(toDate) , {
        CLIENT:query.CLIENT?query.CLIENT :[]
      }, ApiHeaders.getOptions())
  }

  exportDropOffReport(query:Query,type: SystemType, page: number, size: number, sort: boolean, order: string, fromDate: string="", toDate: string="",sortBy: string=""): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/reporting/drop-off-compare/export?type=${type}&page=${page}&size=${size}&sort=${sort}&order=${order}&sortBy=${sortBy}`+ '&fromDate=' + encodeURIComponent(fromDate) + '&toDate=' + encodeURIComponent(toDate) , {
        CLIENT:query.CLIENT?query.CLIENT :[]
      }, ApiHeaders.getOptionsJsonPlain())
  }

  getIndustryUsageReport(fromDate: string, toDate: string, limit?: number): Observable<RestResponse> {
    let url = `${environment.API_URL}/api/reporting/industry-usage?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
    if (limit) {
      url += `&limit=${limit}`;
    }
    return this.http.get<RestResponse>(url, ApiHeaders.getOptions());
  }

  exportIndustryUsageReport(fromDate: string, toDate: string, limit?: number): Observable<any> {
    let url = `${environment.API_URL}/api/reporting/industry-usage/export?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
    if (limit) {
      url += `&limit=${limit}`;
    }
    return this.http.get(url, ApiHeaders.getOptionsJsonPlain());
  }
}
